import React, { useState, useEffect } from 'react'
import {
  useShowContext,
  SimpleShowLayout,
  Show,
  useTranslate,
  Loading,
  Error,
  ImageField,
  RecordContextProvider,
} from 'react-admin'
import { Grid, Typography, Container, Skeleton } from '@mui/material'
import { CardCommon } from '../../../uiElements'
import { FormDivider } from 'src/components/uiElements/fields/Divider'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { isUndefined } from 'src/helper/isUndefined'
import { accountServices } from 'src/services/payment/account/account.service'
import { userServices } from 'src/services/reservation/user/user.service'
import { businessUnitServices } from 'src/services/reservation/businessUnit/businessUnit.service'

const ShowWrapper = ({ children }) => <Container maxWidth='false'> {children}</Container>

const ShowContent = () => {
  const translate = useTranslate()
  const [creditsAgainstLoading, setCreditsInFavorLoading] = useState(false)
  const [creditsInFavorLoading, setCreditsAgainstLoading] = useState(false)
  const [creditsInFavor, setCreditsInFavor] = useState(0)
  const [creditsAgainst, setCreditsAgainst] = useState(0)
  const [creditsAvailableLoading, setCreditsAvailableLoading] = useState(false)
  const [creditsAvailable, setCreditsAvailable] = useState(null)
  const {
    error, // error returned by dataProvider when it failed to fetch the record. Useful if you want to adapt the view instead of just showing a notification using the `onError` side effect.
    isLoading, // boolean that is true until the record is available for the first time
    record, // record fetched via dataProvider.getOne() based on the id from the location
  } = useShowContext()

  useEffect(() => {
    if (record?.hash_business_unit) {
      getTotalBalancesAgainstByBusinessUnitHash(record?.hash_business_unit)
      getTotalCreditsByBusinessUnitHash(record?.hash_business_unit)
      getCreditsByhash(record?.hash_business_unit)
    }
  }, [record?.hash_business_unit])

  const getTotalCreditsByBusinessUnitHash = async (hash) => {
    try {
      setCreditsInFavorLoading(true)
      const totalCredits = await accountServices.getTotalCreditsByBusinessUnitHash(hash)
      if (totalCredits) {
        setCreditsInFavor(totalCredits)
        setCreditsInFavorLoading(false)
        return
      }
      setCreditsInFavorLoading(false)
    } catch (error) {
      setCreditsInFavorLoading(false)
    }
  }

  const getTotalBalancesAgainstByBusinessUnitHash = async (hash) => {
    try {
      setCreditsAgainstLoading(true)
      const totalAgainstCredits = await accountServices.getTotalBalancesAgainstByBusinessUnitHash(hash)
      if (totalAgainstCredits) {
        setCreditsAgainst(totalAgainstCredits)
        setCreditsAgainstLoading(false)
        return
      }
      setCreditsAgainstLoading(false)
    } catch (error) {
      setCreditsAgainstLoading(false)
    }
  }

  const getCommercialResponsible = () => {
    if (record?.salesperson_user_id_relation) {
      return record?.salesperson_user_id_relation?.firstname + ' ' + record?.salesperson_user_id_relation?.surname
    }
    return '-'
  }

  const getCreditsByhash = async (hash) => {
    try {
      setCreditsAvailableLoading(true)
      const credits = await businessUnitServices.getCreditsByBusinessUnitHash(hash)
      if (credits) {
        setCreditsAvailable(credits)
      }
    } catch (error) {
    } finally {
      setCreditsAvailableLoading(false)
    }
  }

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return <Error />
  }

  return (
    <SimpleShowLayout>
      <CardCommon
        title={translate('ra.entity.business-unit')}
        actions={<CardCrudBottomButtons btnEdit={true} />}
      >
        <Grid container spacing={3}>
          <ShowListElement label={translate('ra.field.id')} value={record.id} />

          <ShowListElement label={translate('ra.field.short_name')} value={record.short_name} />
          <ShowListElement label={translate('ra.field.long_name')} value={record.long_name} />
          <ShowListElement
            label={translate('ra.field.parent_businessunit')}
            value={isUndefined(record.parent_relation?.long_name)}
          />
          <ShowListElement label={translate('ra.field.email')} value={record.email} />
          <ShowListElement label={translate('ra.field.phone')} value={isUndefined(record.phone)} />
          <ShowListElement label={translate('ra.field.contact')} value={isUndefined(record.contact)} />

          <ShowListElement
            label={translate('ra.field.description')}
            value={isUndefined(record.description)}
          />
          <ShowListElement label={translate('ra.field.hash')} value={record?.hash_business_unit} />
          <ShowListElement label={translate('ra.field.address')} value={isUndefined(record.address)} />
          <ShowListElement label={translate('ra.field.url')} value={isUndefined(record?.url)} />
          <ShowListElement label={translate('ra.field.country')} value={record.country?.long_name} />
          <ShowListElement label={translate('ra.field.state')} value={isUndefined(record.state)} />
          <ShowListElement
            label={translate('ra.field.config')}
            value={isUndefined(record.config)}
            type={'json'}
          />

          <ShowListElement
            label={translate('ra.field.fantasy_name')}
            value={isUndefined(record.fantasy_name)}
          />

          <ShowListElement
            label={translate('ra.field.business_type')}
            value={record.business_type?.long_name}
          />
          <ShowListElement label={translate('ra.field.work_unit')} value={record.work_unit?.long_name} />
          <ShowListElement
            label={translate('ra.field.business_type')}
            value={record.business_type?.long_name}
          />
          <ShowListElement label={translate('ra.field.token')} value={record.token} />

          <ShowListElement
            label={translate('ra.field.client')}
            value={isUndefined(record.client_relation?.long_name)}
          />
          <ShowListElement
            label={translate('ra.field.manager_responsible')}
            value={record.user ? `${record.user?.firstname} ${record.user?.surname}` : '-'}
          />
          <ShowListElement
            label={translate('ra.field.commercial_responsible')}
            value={isLoading ? 'loading...' : getCommercialResponsible()}
          />
          <ShowListElement label={translate('ra.field.is_agency')} value={record.is_agency} type='boolean' />
          {record.is_agency && (
            <ShowListElement label={translate('ra.field.file_number')} value={record.agency_id} />
          )}
          <ShowListElement label={translate('ra.field.logo')} value={record.logo} type='image' />
          <ShowListElement
            label={translate('ra.field.category')}
            value={isUndefined(record.category)}
          />

          <FormDivider show={true} text={translate('ra.field.fiscal_data')} />
          <ShowListElement label={translate('ra.field.name')} value={record.fiscal_data_relation?.name} />
          <ShowListElement
            label={translate('ra.field.type_responsible')}
            value={record.fiscal_data_relation?.type_responsible_relation?.name}
          />
          <ShowListElement
            label={translate('ra.field.document_type')}
            value={record.fiscal_data_relation?.document_type_relation?.long_name}
          />
          <ShowListElement
            label={translate('ra.field.document_number')}
            value={record.fiscal_data_relation?.document_number}
          />
          <ShowListElement
            label={translate('ra.field.tma_code')}
            value={isUndefined(record.fiscal_data_relation?.tma_code)}
          />

          <FormDivider show={true} text={translate('ra.field.currency')} />
          {record.currency_relation?.length > 0 ? (
            record.currency_relation?.map(
              (field, index) =>
                !field.is_reverse && (
                  <Grid container key={index}>
                    <ShowListElement
                      label={translate('ra.field.currency_from')}
                      value={field.currency_relation?.long_name}
                    />
                    <ShowListElement
                      label={translate('ra.field.currency_to')}
                      value={field.currency_to_relation?.long_name}
                    />
                    <ShowListElement label={translate('ra.field.value')} value={field.value} />
                  </Grid>
                )
            )
          ) : (
            <Grid item>
              <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
            </Grid>
          )}

          <FormDivider show={true} text={translate('ra.field.currency_inheritance')} />
          {record.currency_inheritance?.length > 0 ? (
            record.currency_inheritance?.map(
              (field, index) =>
                !field.is_reverse && (
                  <Grid container key={index}>
                    <ShowListElement
                      label={translate('ra.field.currency_from')}
                      value={field.currency_relation?.long_name}
                    />
                    <ShowListElement
                      label={translate('ra.field.currency_to')}
                      value={field.currency_to_relation?.long_name}
                    />
                    <ShowListElement label={translate('ra.field.value')} value={field.value} />
                  </Grid>
                )
            )
          ) : (
            <Grid item>
              <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
            </Grid>
          )}

          <FormDivider show={true} text={translate('ra.field.products')} />
          {record.product_work_unit_by_business_unit_relation?.length ? (
            <>
              {record.product_work_unit_by_business_unit_relation?.map((field, index) => (
                <Grid container key={index}>
                  <ShowListElement
                    label={translate('ra.field.long_name')}
                    value={field.product_by_work_unit_relation?.product_relation?.long_name}
                  />
                  <ShowListElement
                    label={translate('ra.field.short_name')}
                    value={field.product_by_work_unit_relation.product_relation.short_name}
                  />
                  <ShowListElement
                    label={translate('ra.field.tma_code')}
                    value={field.product_by_work_unit_relation.product_relation.tma_code}
                  />
                  <ShowListElement
                    label={translate('ra.field.token_parent')}
                    value={field.product_by_work_unit_relation.token_id}
                  />
                  <ShowListElement label={translate('ra.field.token')} value={field.token} />
                  <ShowListElement
                    label={translate('ra.field.is_offline')}
                    value={field.product_by_work_unit_relation.product_relation.is_offline}
                    type='boolean'
                  />
                </Grid>
              ))}
            </>
          ) : (
            <Grid item>
              <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
            </Grid>
          )}

          <FormDivider show={true} text={translate('ra.field.products_inheritance')} />
          {record.product_work_unit_by_business_unit_inheritance?.length ? (
            <>
              {record.product_work_unit_by_business_unit_inheritance?.map((field, index) => (
                <Grid container key={index}>
                  <ShowListElement
                    label={translate('ra.field.long_name')}
                    value={field.product_by_work_unit_relation?.product_relation?.long_name}
                  />
                  <ShowListElement
                    label={translate('ra.field.short_name')}
                    value={field.product_by_work_unit_relation.product_relation.short_name}
                  />
                  <ShowListElement
                    label={translate('ra.field.tma_code')}
                    value={field.product_by_work_unit_relation.product_relation.tma_code}
                  />
                  <ShowListElement
                    label={translate('ra.field.token')}
                    value={field.product_by_work_unit_relation.token_id}
                  />
                  <ShowListElement
                    label={translate('ra.field.is_offline')}
                    value={field.product_by_work_unit_relation.product_relation.is_offline}
                    type='boolean'
                  />
                </Grid>
              ))}
            </>
          ) : (
            <Grid item>
              <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
            </Grid>
          )}

          <FormDivider show={true} text={translate('ra.field.products_inheritance_ut')} />
          {record.product_by_work_unit_inheritance?.length ? (
            <>
              {record.product_by_work_unit_inheritance?.map((field, index) => (
                <Grid container key={index}>
                  <ShowListElement
                    label={translate('ra.field.long_name')}
                    value={field.product_relation?.long_name}
                  />
                  <ShowListElement
                    label={translate('ra.field.short_name')}
                    value={field.product_relation.short_name}
                  />
                  <ShowListElement
                    label={translate('ra.field.tma_code')}
                    value={field.product_relation.tma_code}
                  />
                  <ShowListElement label={translate('ra.field.token')} value={field.token_id} />
                  <ShowListElement
                    label={translate('ra.field.is_offline')}
                    value={field.product_relation.is_offline}
                    type='boolean'
                  />
                </Grid>
              ))}
            </>
          ) : (
            <Grid item>
              <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
            </Grid>
          )}

          <FormDivider show={true} text={translate('ra.field.children')} />
          {record.children?.length ? (
            <>
              {record.children?.map((field, index) => (
                <Grid container key={index}>
                  <ShowListElement label={translate('ra.field.token')} value={field.token} />
                  <ShowListElement label={translate('ra.field.long_name')} value={field.long_name} />
                  <ShowListElement label={translate('ra.field.short_name')} value={field.short_name} />
                  <ShowListElement label={translate('ra.field.description')} value={field.description} />
                  <ShowListElement
                    label={translate('ra.field.is_active')}
                    value={field.is_active}
                    type='boolean'
                  />
                  <FormDivider show={false} />
                </Grid>
              ))}
            </>
          ) : (
            <Grid item>
              <Typography variant='button'>{translate('ra.message.no_registry')}</Typography>
            </Grid>
          )}
          <FormDivider show={true} text={translate('ra.title.pay_condition')} />
          <Grid container>
            {creditsInFavorLoading ? (
              <Skeleton variant='rectangular' width={'100%'} height={20} />
            ) : (
              <ShowListElement label={translate('ra.title.credits')} value={creditsInFavor} />
            )}
            {creditsAgainstLoading ? (
              <Skeleton variant='rectangular' width={'100%'} height={20} />
            ) : (
              <ShowListElement label={translate('ra.title.credit_against')} value={creditsAgainst} />
            )}
          </Grid>

          <FormDivider show={true} text={translate('ra.title.credits')} />
          <Grid container>
            {creditsAvailableLoading ? (
              <Skeleton variant='rectangular' width={'100%'} height={20} />
            ) : (
              <ShowListElement
                label={translate('ra.field.available_credits')}
                value={creditsAvailable?.credit}
              />
            )}
            {creditsAvailableLoading ? (
              <Skeleton variant='rectangular' width={'100%'} height={20} />
            ) : (
              <ShowListElement
                label={translate('ra.field.has_credit')}
                value={creditsAvailable?.has_credit}
                type={'boolean'}
              />
            )}
            {creditsAvailableLoading ? (
              <Skeleton variant='rectangular' width={'100%'} height={20} />
            ) : (
              <ShowListElement
                label={translate('ra.field.limit_credit')}
                value={creditsAvailable?.limit_credit}
              />
            )}
          </Grid>
        </Grid>
      </CardCommon>
    </SimpleShowLayout>
  )
}

export const BusinessUnitShow = (props) => {
  return (
    <Show component={ShowWrapper} {...props} className='CrudShowContainer' actions={null}>
      <ShowContent {...props} />
    </Show>
  )
}
